import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Upload, Button, Modal, Form, Input, Select, message } from "antd";
import { ApolloError } from "@apollo/client";

import { AttachmentType, AttachmentTypeDisplayNames, PropertyDocumentsType, PropertyDocumentsTypeDisplayNames, warrantyType } from "constants/field-values";
import AttachmentItem from "components/attachment-item/attachment-item";
import { useSelectedAttachments } from "state/reactive-vars/selected-attachment-var";
import { useUpdateAttachmentByIdMutation } from "graphql/mutations/update-attachment-by-id.generated";
import { useUpdatePropertyDocumentByIdMutation } from "graphql/mutations/update-property-documents-by-id.generated";
import { createBeforeUpload } from "utils/files-management";

interface AddAttachmentModalProps {
	open: boolean;
	onCancel: () => void;
	updateAttachments: (element: any, warranty?: boolean) => void;
	onConfirm?: () => Promise<void>;
	isEmail?: boolean;
	editMode?: boolean;
	editProduct?: boolean;
	propertyDocMode?: boolean;
	warrantyDocMode?: boolean;
	filesLimit?: number;
	fixedCategory?: PropertyDocumentsType | string;
}

const AddAttachmentModal = (props: AddAttachmentModalProps) => {
	const {
		open,
		onCancel,
		updateAttachments,
		onConfirm,
		isEmail = false,
		editMode,
		editProduct,
		propertyDocMode = false,
		warrantyDocMode = false,
		filesLimit = 5,
		fixedCategory
	} = props;

	const { selectedAttachmentsValue } = useSelectedAttachments();

	const [loading, setLoading] = useState(false);
	const [loadingConfirm, setLoadingConfirm] = useState(false);
	const [fileList, setFileList] = useState<any[]>([]);
	const [attachmentType, setAttachmentType] = useState<string>("");
	const [name, setName] = useState<string>("");
	const [attachmentDescription, setAttachmentDescription] = useState<string>("");
	// New state for multiple files - each file has its own name and category
	const [files, setFiles] = useState<
		Array<{
			uid: string;
			file: any;
			name: string;
			type: string;
		}>
	>([]);

	useEffect(() => {
		if (!open) {
			resetState();
		}
	}, [open]);

	useEffect(() => {
		if (editMode && selectedAttachmentsValue) {
			setFileList([selectedAttachmentsValue.file]);
			setAttachmentType(selectedAttachmentsValue.type);

			const attachmentNameInitialValue = selectedAttachmentsValue.name ? selectedAttachmentsValue.name : "";

			setName(attachmentNameInitialValue);
		}
	}, [editMode, selectedAttachmentsValue]);

	useEffect(() => {
		if (fixedCategory) {
			setAttachmentType(fixedCategory);
		} else if (warrantyDocMode) {
			setAttachmentType(warrantyType);
		}
	}, [fixedCategory, warrantyDocMode]);

	const resetState = useCallback(() => {
		setFileList([]);
		setAttachmentType("");
		setAttachmentDescription("");
		setName("");
		setLoading(false);
		setFiles([]);
	}, []);

	const handleOnClose = useCallback(() => {
		onCancel();
		resetState();
	}, [onCancel, resetState]);

	const handleOnCompleteUpdateAttachmentMutation = useCallback(
		(data: any) => {
			handleOnClose();
		},
		[handleOnClose]
	);

	const handleOnErrorUpdateAttachmentMutation = useCallback((error: ApolloError) => {
		console.log("useUpdateAttachmentByIdMutation Error: ", error);
	}, []);

	const [updateAttachmentByIdMutation, { loading: loadingUpdateAttachment }] = useUpdateAttachmentByIdMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorUpdateAttachmentMutation,
		onCompleted: handleOnCompleteUpdateAttachmentMutation,
		refetchQueries: ["getProductById", "getProducts"]
	});

	const [updatePropertyDocumentByIdMutation, { loading: loadingUpdatePropertyDocument }] =
		useUpdatePropertyDocumentByIdMutation({
			errorPolicy: "none",
			fetchPolicy: "network-only",
			notifyOnNetworkStatusChange: true,
			onError: handleOnErrorUpdateAttachmentMutation,
			onCompleted: handleOnCompleteUpdateAttachmentMutation,
			refetchQueries: ["getPropertyById", "getPropertiesFiltered", "getPropertiesByWorkspaceId"]
		});

	// Helper function to auto-select category based on file type
	const getAutoSelectedCategory = useCallback(
		(file: any): string => {
			if (fixedCategory) return fixedCategory;
			const fileName = file.name?.toLowerCase() || "";
			const fileType = file.type?.toLowerCase() || "";

			if (propertyDocMode) {
				// Auto-select for property documents
				if (
					fileName.endsWith(".dwg") ||
					fileName.endsWith(".dxf") ||
					fileName.endsWith(".rvt") ||
					fileName.endsWith(".skp") ||
					fileType.includes("cad") ||
					fileType.includes("drawing")
				) {
					return PropertyDocumentsType.DESIGN;
				} else if (fileName.endsWith(".pdf") && (fileName.includes("markup") || fileName.includes("plan"))) {
					if (fileName.includes("floor") || fileName.includes("floorplan")) {
						return PropertyDocumentsType.FLOOR_PLAN;
					}
					return PropertyDocumentsType.MARKUPS;
				} else {
					return PropertyDocumentsType.OTHER;
				}
			} else {
				// Auto-select for general attachments
				if (
					fileName.endsWith(".pdf") ||
					fileType === "application/pdf" ||
					fileName.endsWith(".doc") ||
					fileName.endsWith(".docx") ||
					fileType.includes("document")
				) {
					if (fileName.includes("manual") || fileName.includes("guide") || fileName.includes("instruction")) {
						return AttachmentType.MANUAL;
					} else if (fileName.includes("technical") || fileName.includes("spec") || fileName.includes("datasheet")) {
						return AttachmentType.TECHNICAL_DOCUMENT;
					} else {
						return AttachmentType.GENERAL_DOCUMENT;
					}
				} else if (
					fileName.endsWith(".mp4") ||
					fileName.endsWith(".avi") ||
					fileName.endsWith(".mov") ||
					fileName.endsWith(".wmv") ||
					fileType.includes("video")
				) {
					return AttachmentType.VIDEO;
				} else if (
					fileName.endsWith(".jpg") ||
					fileName.endsWith(".jpeg") ||
					fileName.endsWith(".png") ||
					fileName.endsWith(".gif") ||
					fileName.endsWith(".bmp") ||
					fileName.endsWith(".heic") ||
					fileName.endsWith(".heif") ||
					fileType.includes("image")
				) {
					return AttachmentType.PHOTO;
				} else if (
					fileName.endsWith(".mp3") ||
					fileName.endsWith(".wav") ||
					fileName.endsWith(".ogg") ||
					fileName.endsWith(".m4a") ||
					fileType.includes("audio")
				) {
					return AttachmentType.AUDIO;
				} else if (
					fileName.endsWith(".dwg") ||
					fileName.endsWith(".dxf") ||
					fileName.endsWith(".svg") ||
					fileType.includes("drawing") ||
					fileType.includes("diagram")
				) {
					return AttachmentType.DIAGRAM;
				} else {
					return AttachmentType.GENERAL_DOCUMENT;
				}
			}
		},
		[propertyDocMode, fixedCategory]
	);

	const handleChange = useCallback(
		(info: any) => {
			const { fileList } = info;

			// Limit number of files
			const limitedFileList = fileList.slice(-filesLimit);
			setFileList(limitedFileList);

			if (!editMode) {
				// Create file objects with auto-selected categories
				const newFiles = limitedFileList.map((file: any) => {
					// Check if file already exists in files array
					const existingFile = files.find(f => f.uid === file.uid);

					if (existingFile) {
						// this function runs twice on upload, the second time being the converted file
						// so update the file with the converted version
						if (/(heic|heif)$/i.test(existingFile.file.name)) {
							return {
								...existingFile,
								file: file
							};
						}
						return existingFile;
					}

					// New file - auto-select category and use original filename as name
					const autoCategory = getAutoSelectedCategory(file);
					const fileName = file.name || "";
					// Remove file extension from name
					const nameWithoutExtension = fileName.replace(/\.[^/.]+$/, "");

					return {
						uid: file.uid,
						file: file,
						name: nameWithoutExtension,
						type: autoCategory
					};
				});

				setFiles(newFiles);
			}
		},
		[editMode, files, getAutoSelectedCategory]
	);

	const handleRemove = useCallback((file: any) => {
		setFileList(prev => prev.filter(f => f.uid !== file.uid));
		setFiles(prev => prev.filter(f => f.uid !== file.uid));
	}, []);

	const updateFileName = useCallback((uid: string, newName: string) => {
		setFiles(prev => prev.map(file => (file.uid === uid ? { ...file, name: newName } : file)));
	}, []);

	const updateFileType = useCallback((uid: string, newType: string) => {
		setFiles(prev => prev.map(file => (file.uid === uid ? { ...file, type: newType } : file)));
	}, []);

	const customRequest = useCallback((args: any) => {
		setTimeout(() => {
			args.onSuccess?.("ok", {} as File);
		}, 0);
	}, []);

	const handleConfirmAttachment = useCallback(async () => {
		if (editProduct) {
			if (selectedAttachmentsValue && selectedAttachmentsValue.id) {
				if (selectedAttachmentsValue.name !== name || selectedAttachmentsValue.type !== attachmentType) {
					if (propertyDocMode) {
						updatePropertyDocumentByIdMutation({
							variables: {
								set: {
									type: attachmentType,
									name: name
								},
								id: selectedAttachmentsValue.id
							}
						});
					} else {
						updateAttachmentByIdMutation({
							variables: {
								set: {
									type: attachmentType,
									name: name
								},
								id: selectedAttachmentsValue.id
							}
						});
					}
				} else {
					handleOnClose();
				}
			} else {
				// Handle multiple files upload
				if (files.length > 0) {
					files.forEach(fileData => {
						const newAttachment = {
							uid: fileData.uid,
							type: fileData.type,
							description: attachmentDescription,
							file: fileData.file,
							name: fileData.name
						};

						updateAttachments(newAttachment, warrantyDocMode);
					});
					handleOnClose();
				}
			}
		} else {
			if (isEmail) {
				if (fileList.length > 0) {
					const { originFileObj, name: fileName, size, type, uid } = fileList[0];

					const newAttachment = {
						uid: uid,
						file: originFileObj,
						name: name,
						size,
						type
					};

					updateAttachments(newAttachment, warrantyDocMode);
					setLoadingConfirm(true);
					await onConfirm?.();
					setLoadingConfirm(false);
					handleOnClose();
				}
			} else {
				// Handle multiple files upload
				if (files.length > 0) {
					files.forEach(fileData => {
						const newAttachment = {
							uid: fileData.uid,
							type: fileData.type,
							description: attachmentDescription,
							file: fileData.file,
							name: fileData.name
						};

						updateAttachments(newAttachment, warrantyDocMode);
					});
					setLoadingConfirm(true);
					await onConfirm?.();
					setLoadingConfirm(false);
					handleOnClose();
				}
			}
		}
	}, [
		files,
		fileList,
		attachmentType,
		handleOnClose,
		updateAttachments,
		onConfirm,
		name,
		editProduct,
		propertyDocMode,
		selectedAttachmentsValue,
		warrantyDocMode,
		attachmentDescription,
		isEmail
	]);

	const isLoading = loading || loadingConfirm || loadingUpdateAttachment || loadingUpdatePropertyDocument;

	const typeOptions = useMemo(() => {
		if (propertyDocMode) {
			if (warrantyDocMode) {
				return [{ value: warrantyType, label: warrantyType }];
			} else if (fixedCategory) {
				return [{ value: fixedCategory, label: PropertyDocumentsTypeDisplayNames[fixedCategory as PropertyDocumentsType] || fixedCategory }];
			} else {
				return Object.values(PropertyDocumentsType).map(element => {
					return { value: element, label: PropertyDocumentsTypeDisplayNames[element as PropertyDocumentsType] };
				});
			}
		} else {
			return Object.values(AttachmentType)
				.filter(type => type !== AttachmentType.GUIDE)
				.map(type => ({ value: type, label: AttachmentTypeDisplayNames[type] }));
		}
	}, [propertyDocMode, warrantyDocMode, fixedCategory]);

	return (
		<Modal
			open={open}
			onCancel={handleOnClose}
			footer={null}
			className="add-product-attachment-modal"
			destroyOnClose={true}
		>
			<div className="add-product-attachment-modal-content">
				<span className="add-product-attachment-title">
					{propertyDocMode ? "Upload Property Resource" : "Select files to upload"}
				</span>
				<Form className="add-product-attachment-modal-form" autoComplete="off">
					{isEmail ? (
						<Form.Item
							name={"file"}
							className="login-form-item"
							rules={[
								{
									required: true,
									message: "Please select an Attachment Type."
								}
							]}
							required
							validateTrigger="onBlur"
						>
							<Upload
								name={"file"}
								multiple={false}
								fileList={fileList}
								onChange={handleChange}
								onRemove={handleRemove}
								customRequest={customRequest}
								accept="*/*"
								disabled={editMode}
								beforeUpload={createBeforeUpload()}
							>
								<Button className="upload-attachment-button">Add files</Button>
							</Upload>
						</Form.Item>
					) : editMode ? (
						<>
							<Form.Item
								className="login-form-item"
								rules={[{ required: true, message: "Please input the device name." }]}
								required
								validateTrigger="onBlur"
							>
								<Input
									name="name"
									placeholder="Name"
									className="add-product-attachment-input"
									value={name}
									autoComplete="off"
									onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
										setName(event.target.value);
									}}
									allowClear
									disabled={isLoading}
								/>
							</Form.Item>

							{fileList && fileList.length > 0 && (
								<div className="selected-file-container">
									<span className="selected-file-title">Select File</span>
									<AttachmentItem
										file={fileList[0]}
										onRemove={() => setFileList([])}
										type={attachmentType}
										editMode={editMode}
										name={name}
									/>
								</div>
							)}

							<span className="selected-file-title">Choose Category</span>

							<Form.Item
								className="select-category-form-item"
								rules={[
									{
										required: true,
										message: "Please select an Attachment Type."
									}
								]}
								required
								validateTrigger="onBlur"
							>
								<Select
									value={attachmentType}
									defaultActiveFirstOption={warrantyDocMode ? true : false}
									className="add-product-attachment-input"
									onChange={(value: string) => setAttachmentType(value)}
									options={typeOptions}
									placeholder={propertyDocMode ? "Document Type" : "Attachment Type"}
									allowClear
									disabled={isLoading || warrantyDocMode || !!fixedCategory}
									suffixIcon={
										<img
											src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
											alt="dropdown icon"
											style={{ width: "1em", height: "1em" }}
										/>
									}
								/>
							</Form.Item>
						</>
					) : (
						<>
							{/* Upload button - always visible for new uploads */}
							<Form.Item name={"file"} className="login-form-item" validateTrigger="onBlur">
								<Upload
									name={"file"}
									multiple={true}
									fileList={fileList}
									onChange={handleChange}
									onRemove={handleRemove}
									customRequest={customRequest}
									accept="*/*"
									disabled={fileList.length >= filesLimit}
									showUploadList={false}
									beforeUpload={createBeforeUpload()}
								>
									<Button className="upload-attachment-button" disabled={fileList.length >= filesLimit}>
										Add files {fileList.length > 0 && `(${fileList.length}/${filesLimit})`}
									</Button>
								</Upload>
							</Form.Item>

							{/* Display each uploaded file with its name and category */}
							{files.map((fileData, index) => (
								<div
									key={fileData.uid}
									style={{
										marginBottom: "20px",
										padding: "15px",
										border: "1px solid #d9d9d9",
										borderRadius: "4px"
									}}
								>
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											marginBottom: "10px"
										}}
									>
										<span style={{ fontWeight: 500 }}>
											File {index + 1}: {fileData.file.name}
										</span>
										<Button type="text" danger onClick={() => handleRemove(fileData.file)} size="small">
											Remove
										</Button>
									</div>

									<Form.Item label="Name" required style={{ marginBottom: "12px" }}>
										<Input
											placeholder="Name"
											className="add-product-attachment-input"
											value={fileData.name}
											onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
												updateFileName(fileData.uid, event.target.value);
											}}
											allowClear
											disabled={isLoading}
										/>
									</Form.Item>

									<Form.Item label="Category" required style={{ marginBottom: "0" }}>
										<Select
											value={fileData.type}
											className="add-product-attachment-input"
											onChange={(value: string) => updateFileType(fileData.uid, value)}
											options={typeOptions}
											placeholder={propertyDocMode ? "Document Type" : "Attachment Type"}
											allowClear
											disabled={isLoading || warrantyDocMode || !!fixedCategory}
											suffixIcon={
												<img
													src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
													alt="dropdown icon"
													style={{ width: "1em", height: "1em" }}
												/>
											}
										/>
									</Form.Item>
								</div>
							))}
						</>
					)}

					<div className="upload-button-container">
						<Button
							type="primary"
							className="upload-button"
							onClick={handleConfirmAttachment}
							disabled={
								isEmail
									? !fileList.length
									: editMode
										? !fileList.length || attachmentType === ""
										: !files.length || files.some(f => !f.name || !f.type)
							}
							loading={isLoading}
							style={{ marginTop: 16 }}
						>
							{isLoading ? "Loading" : "Confirm"}
						</Button>
					</div>
				</Form>
			</div>
		</Modal>
	);
};

export default AddAttachmentModal;
